interface UploadedFile {
  key: string;
  type: string;
  name: string;
}

interface PresignedUrlData {
  uploadUrl: string;
  key: string;
}

/**
 * Uploads a single file to S3 using a presigned URL
 * @param file - The file to upload
 * @param presignedUrlData - The presigned URL data containing uploadUrl and key
 * @returns Promise<UploadedFile> - The uploaded file metadata
 */
export const uploadFileToS3 = async (file: File, presignedUrlData: PresignedUrlData): Promise<UploadedFile> => {
  const uploadResponse = await fetch(presignedUrlData.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload ${file.name}: ${uploadResponse.status} ${uploadResponse.statusText}`);
  }

  return {
    key: presignedUrlData.key,
    type: file.type,
    name: file.name,
  };
};

/**
 * Uploads multiple files to S3 using presigned URLs
 * @param files - Array of files to upload
 * @param presignedUrls - Array of presigned URL data
 * @returns Promise<UploadedFile[]> - Array of uploaded file metadata
 */
export const uploadFilesToS3 = async (files: File[], presignedUrls: PresignedUrlData[]): Promise<UploadedFile[]> => {
  if (files.length !== presignedUrls.length) {
    throw new Error('Mismatch between number of files and presigned URLs');
  }

  const uploadPromises = files.map((file, index) => {
    const presignedUrlData = presignedUrls[index];
    if (!presignedUrlData) {
      throw new Error(`No upload URL found for file ${file.name}`);
    }
    return uploadFileToS3(file, presignedUrlData);
  });

  return Promise.all(uploadPromises);
};
